/* ============================================================================
  Gift Box Builder V2 (Dawn) - REDESIGNED VERSION v3
  - Horizontal layout with sticky product header
  - Supports 2-4 boxes dynamically
  - 3-column product grid
  - Cart count shows bundle as 1 item (FIXED)
============================================================================ */

class GiftBoxBuilderV2 {
  formatMoney(cents) {
  const format =
    (window.giftBoxSettings && window.giftBoxSettings.moneyFormat) || "${{amount}}";

  const value = Number(cents || 0);

  const placeholderRegex = /\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/;

  const formatWithDelimiters = (amount, precision, thousands, decimal) => {
    precision = precision == null ? 2 : precision;
    thousands = thousands || ",";
    decimal = decimal || ".";

    const number = (amount / 100).toFixed(precision);
    const parts = number.split(".");
    const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const centsPart = parts[1] ? decimal + parts[1] : "";
    return dollars + centsPart;
  };

  const match = format.match(placeholderRegex);
  const placeholder = match ? match[1] : "amount";

  switch (placeholder) {
    case "amount":
      return format.replace(placeholderRegex, formatWithDelimiters(value, 2, ",", "."));
    case "amount_no_decimals":
      return format.replace(placeholderRegex, formatWithDelimiters(value, 0, ",", "."));
    case "amount_with_comma_separator":
      return format.replace(placeholderRegex, formatWithDelimiters(value, 2, ".", ","));
    case "amount_no_decimals_with_comma_separator":
      return format.replace(placeholderRegex, formatWithDelimiters(value, 0, ".", ","));
    default:
      return format.replace(placeholderRegex, formatWithDelimiters(value, 2, ",", "."));
  }
}

  constructor(root) {
    this.root = root;
    this.selectedProducts = {};
    this.currentActiveBox = 1;
    this._fetchController = null;
    
    // Determine how many boxes are active
    this.totalBoxes = window.giftBoxSettings?.totalBoxes || 2;
    
    // Initialize selectedProducts for all boxes
    for (let i = 1; i <= this.totalBoxes; i++) {
      this.selectedProducts[`box${i}`] = null;
    }

    this.cacheElements();
    this.bindEvents();

    this.activateBox(1, { forceLoad: true });
    this.updatePriceUI();
    this.updateATCState();
  }

  cacheElements() {
    this.els = {
      boxes: Array.from(this.root.querySelectorAll('.gift-box')),
      grid: this.root.querySelector('.products-grid[data-product-grid]'),
      activeBoxTitle: this.root.querySelector('[data-active-box-title]'),
      atcBtn: this.root.querySelector('[data-add-bundle]'),
      bundlePrice: this.root.querySelector('[data-bundle-price]'),
      savings: this.root.querySelector('[data-savings]'),
    };
  }

  bindEvents() {
    this.els.boxes.forEach((box) => {
      const num = parseInt(box.dataset.box, 10);

      box.addEventListener('click', (e) => {
        if (e.target.closest('[data-remove-product]')) return;
        this.activateBox(num);
      });

      const removeBtn = box.querySelector('[data-remove-product]');
      removeBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeProductFromBox(num);
      });

      const selectBtn = box.querySelector('[data-select-btn]');
      selectBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.activateBox(num, { forceLoad: true });
      });
    });

    this.els.atcBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.els.atcBtn.disabled) return;
      this.addBundleToCart();
    });
  }

  activateBox(boxNumber, opts = {}) {
    const { forceLoad = false } = opts;
    const box = this.root.querySelector(`.gift-box[data-box="${boxNumber}"]`);
    if (!box) return;
    if (box.classList.contains('disabled')) return;
    if (this.currentActiveBox === boxNumber && !forceLoad) return;

    this.currentActiveBox = boxNumber;

    this.els.boxes.forEach((b) => b.classList.remove('active'));
    box.classList.add('active');

    const title = box.querySelector('.box-title')?.textContent?.trim() || '';
    if (this.els.activeBoxTitle) this.els.activeBoxTitle.textContent = title;

    const handle = (box.dataset.collection || '').trim();
    if (!handle) {
      this.renderGridMessage('No collection assigned for this box.');
      return;
    }

    this.loadCollectionProducts(handle);
  }

  async loadCollectionProducts(handle) {
    if (!this.els.grid) {
      console.error('GiftBox: products grid not found. Expect .products-grid[data-product-grid]');
      return;
    }

    if (this._fetchController) this._fetchController.abort();
    this._fetchController = new AbortController();

    this.renderGridLoading();

    try {
      const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=50`;
      const res = await fetch(url, { signal: this._fetchController.signal });
      if (!res.ok) throw new Error(`Products load failed: ${res.status}`);

      const data = await res.json();
      const products = Array.isArray(data.products) ? data.products : [];

      if (!products.length) {
        this.renderGridMessage('No products found.');
        return;
      }

      this.els.grid.innerHTML = products.map((p) => this.productCardHTML(p)).join('');

      this.els.grid.querySelectorAll('[data-add-to-box]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const raw = btn.getAttribute('data-product');
          if (!raw) return;
          let payload;
          try { payload = JSON.parse(raw); } catch { return; }
          this.selectProductForActiveBox(payload);
        });
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error(err);
      this.renderGridMessage('Failed to load products.');
    }
  }

  renderGridLoading() {
    if (!this.els.grid) return;
    this.els.grid.innerHTML = `
      <div class="products-loading">
        <div class="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    `;
  }

  renderGridMessage(msg) {
    if (!this.els.grid) return;
    this.els.grid.innerHTML = `<p style="padding:12px 0; grid-column: 1/-1; text-align:center;">${this.escapeHtml(msg)}</p>`;
  }

  productCardHTML(p) {
    const img = p?.images?.[0]?.src || '';
    const variant = (p.variants || []).find(v => v.available) || (p.variants || [])[0];
    const variantId = variant?.id || null;
    const priceCents = variant?.price ? Math.round(parseFloat(variant.price) * 100) : 0;

    const boxNo = this.currentActiveBox;
    const boxInfo = window.giftBoxSettings?.boxInfo?.[boxNo] || {};
    const enableInfo = !!boxInfo.enable;
    const infoMode = boxInfo.mode || "hover";
    const infoLabel = boxInfo.label || "";
    const cleanDesc = (p.body_html || "").replace(/<[^>]*>/g, "").trim();
    const tooltipText = cleanDesc || (boxInfo.fallback || "No details available.");


    const payload = {
      handle: p.handle,
      title: p.title,
      image: img,
      variantId,
      price: priceCents
    };
    const priceText = this.formatMoney(priceCents);

    const infoBtnHtml = enableInfo
  ? `
  <div class="tooltip-box">
    <button type="button"
      class="product-info-trigger"
      data-info-mode="${infoMode}"
      aria-label="Product details"
    >
      <span class="info-icon">!</span>
      ${infoLabel ? `<span class="info-text">${this.escapeHtml(infoLabel)}</span>` : ``}
    </button>
    <div class="product-tooltip">
      <strong>${this.escapeHtml(p.title)}</strong>
      <p>${this.escapeHtml(tooltipText)}</p>
    </div>
    </div>
  `
  : ``;



    return `
      <div class="product-card-grid">
        <div class="product-image-grid">
          ${img ? `<img src="${img}" alt="${this.escapeHtml(p.title)}" loading="lazy">` : ``}
        </div>

        <div class="product-title-row">
          <h4 class="product-title-grid">${this.escapeHtml(p.title)}</h4>
          ${infoBtnHtml}
        </div>


        <p class="product-price-grid" data-box-price="${this.currentActiveBox}">${priceText}</p>
        <button
          type="button"
          class="btn-add-to-box-grid"
          ${!variantId ? 'disabled' : ''}
          data-add-to-box
          data-product='${this.safeJsonAttr(payload)}'
        >
          Add to Box
        </button>
      </div>
    `;
  }

  safeJsonAttr(obj) {
    return JSON.stringify(obj)
      .replaceAll('&', '&amp;')
      .replaceAll("'", '&#39;')
      .replaceAll('"', '&quot;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }

  selectProductForActiveBox(product) {
    const boxNumber = this.currentActiveBox;
    const key = `box${boxNumber}`;

    if (!product?.variantId) {
      alert('This product is not available.');
      return;
    }

    this.selectedProducts[key] = product;

    const box = this.root.querySelector(`.gift-box[data-box="${boxNumber}"]`);
    if (box) {
      box.classList.add('filled');
      const selected = box.querySelector('[data-selected-product]');
      const selectBtn = box.querySelector('[data-select-btn]');

      if (selected) {
        selected.style.display = '';
        const img = selected.querySelector('.preview-image');
        if (img) { img.src = product.image || ''; img.alt = product.title || ''; }
        const t = selected.querySelector('.product-title-preview');
        if (t) t.textContent = product.title || '';
        const pr = selected.querySelector('.product-price-preview');
        if (pr) pr.textContent = this.formatMoney(product.price);

      }
      if (selectBtn) selectBtn.style.display = 'none';
    }

    // Enable next box if exists and not already filled
    const nextBoxNumber = boxNumber + 1;
    if (nextBoxNumber <= this.totalBoxes) {
      const nextBox = this.root.querySelector(`.gift-box[data-box="${nextBoxNumber}"]`);
      if (nextBox && !this.selectedProducts[`box${nextBoxNumber}`]) {
        nextBox.classList.remove('disabled');
        this.activateBox(nextBoxNumber, { forceLoad: true });
      }
    }

    this.updatePriceUI();
    this.updateATCState();
  }

  removeProductFromBox(boxNumber) {
    const key = `box${boxNumber}`;
    this.selectedProducts[key] = null;

    const box = this.root.querySelector(`.gift-box[data-box="${boxNumber}"]`);
    if (box) {
      box.classList.remove('filled');
      const selected = box.querySelector('[data-selected-product]');
      const selectBtn = box.querySelector('[data-select-btn]');
      if (selected) selected.style.display = 'none';
      if (selectBtn) selectBtn.style.display = '';
    }

    // Disable all boxes after this one
    for (let i = boxNumber + 1; i <= this.totalBoxes; i++) {
      const nextBox = this.root.querySelector(`.gift-box[data-box="${i}"]`);
      if (nextBox) {
        nextBox.classList.add('disabled');
      }
    }

    this.updatePriceUI();
    this.updateATCState();
    this.activateBox(boxNumber, { forceLoad: true });
  }

  updatePriceUI() {
    let total = 0;
    for (let i = 1; i <= this.totalBoxes; i++) {
      total += this.selectedProducts[`box${i}`]?.price || 0;
    }

    if (this.els.bundlePrice) {
      this.els.bundlePrice.textContent = this.formatMoney(total);
    }
    if (this.els.savings) {
      // this.els.savings.textContent = `₹0 savings`;
      this.els.savings.textContent = `${this.formatMoney(0)} savings`;
    }
  }

  updateATCState() {
    // Check if all boxes have products selected
    let allFilled = true;
    for (let i = 1; i <= this.totalBoxes; i++) {
      if (!this.selectedProducts[`box${i}`]) {
        allFilled = false;
        break;
      }
    }

    if (!this.els.atcBtn) return;
    this.els.atcBtn.disabled = !allFilled;
    this.els.atcBtn.classList.toggle('disabled', !allFilled);
  }

// async addBundleToCart() {
//   const BUNDLE_VARIANT_ID = 48189250601175;
//   const drawer = document.querySelector("cart-drawer");
//   const PROXY_URL = "/apps/product-bundle/update-price";

//   const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

//   const safeJson = async (res) => {
//     const t = await res.text();
//     try {
//       return JSON.parse(t);
//     } catch {
//       return null;
//     }
//   };

//   const getCart = async () =>
//     (await fetch("/cart.js", { credentials: "same-origin" })).json();

//   const validateVariant = async (id) => {
//     const r = await fetch(`/variants/${id}.js`, {
//       credentials: "same-origin",
//     });
//     return r.ok ? r.json() : null;
//   };

//   const callProxy = async (bundleTotalCents) => {
//     const r = await fetch(PROXY_URL, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({
//         variant_id: String(BUNDLE_VARIANT_ID),
//         price: (bundleTotalCents / 100).toFixed(2),
//       }),
//     });
//     if (!r.ok) throw new Error("Price update failed");
//   };

//   try {
//     const variant = await validateVariant(BUNDLE_VARIANT_ID);
//     if (!variant || !variant.available)
//       throw new Error("Bundle variant unavailable");

//     let bundleTotalCents = 0;
//     const properties = { _is_bundle: "true" };

//     for (let i = 1; i <= this.totalBoxes; i++) {
//       const p = this.selectedProducts["box" + i];
//       if (!p) {
//         alert("Please select all products");
//         return;
//       }

//       bundleTotalCents += Math.round(Number(p.price) || 0);
//       if (i === 1) properties["Candle"] = p.title;
//       if (i === 2) properties["Scent"] = p.title;
//     }

//     this.els.atcBtn.disabled = true;
//     this.els.atcBtn.textContent = "Updating price...";

//     // 1️⃣ Update price via proxy
//     await callProxy(bundleTotalCents);

//     // 2️⃣ Get cart
//     const cart = await getCart();
//     const existing = cart.items.find(
//       (i) => String(i.id) === String(BUNDLE_VARIANT_ID)
//     );

//     let res;

//     // 🔥 CRITICAL FIX HERE
//     if (existing && Number.isInteger(existing.line)) {
//       // ✅ UPDATE EXISTING LINE
//       res = await fetch("/cart/change.js", {
//         method: "POST",
//         credentials: "same-origin",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           line: existing.line,
//           quantity: 1,
//           properties,
//           sections: "cart-drawer,cart-icon-bubble",
//           sections_url: window.location.pathname,
//         }),
//       });
//     } else {
//       // ✅ ADD NEW PRODUCT
//       res = await fetch("/cart/add.js", {
//         method: "POST",
//         credentials: "same-origin",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           id: BUNDLE_VARIANT_ID,
//           quantity: 1,
//           properties,
//           sections: "cart-drawer,cart-icon-bubble",
//           sections_url: window.location.pathname,
//         }),
//       });
//     }

//     const data = await safeJson(res);
//     if (!res.ok) {
//       console.error("Cart error response:", data);
//       throw new Error("Add to cart failed");
//     }

//     // 3️⃣ Inject updated sections
//     if (data && data.sections) {
//       Object.entries(data.sections).forEach(([id, html]) => {
//         const el = document.getElementById(id);
//         if (el) el.innerHTML = html;
//       });
//     }

//     // 4️⃣ Trigger Dawn cart events
//     document.documentElement.dispatchEvent(
//       new CustomEvent("cart:updated", { bubbles: true })
//     );
//     document.documentElement.dispatchEvent(
//       new CustomEvent("cart:refresh", { bubbles: true })
//     );

//     await sleep(50);
//     drawer?.open?.();
//   } catch (e) {
//     console.error("Bundle Add Error:", e);
//     alert(e.message || "Something went wrong");
//   } finally {
//     this.els.atcBtn.disabled = false;
//     this.els.atcBtn.textContent = "Add to Cart";
//   }
// }

async addBundleToCart() {
  const BUNDLE_VARIANT_ID = 48189250601175;
  const drawer = document.querySelector("cart-drawer");
  const PROXY_URL = "/apps/product-bundle/update-price";

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const safeJson = async (res) => {
    const t = await res.text();
    try { return JSON.parse(t); } catch { return null; }
  };

  const getCart = async () =>
    (await fetch("/cart.js", { credentials: "same-origin" })).json();

  const validateVariant = async (id) => {
    const r = await fetch(`/variants/${id}.js`, { credentials: "same-origin" });
    return r.ok ? r.json() : null;
  };

  const callProxy = async (bundleTotalCents) => {
    const r = await fetch(PROXY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        variant_id: String(BUNDLE_VARIANT_ID),
        price: (bundleTotalCents / 100).toFixed(2),
      }),
    });

    if (!r.ok) {
      const err = await safeJson(r);
      console.error("Proxy error:", err);
      throw new Error("Price update failed");
    }
  };

  try {
    // ✅ validate bundle variant
    const variant = await validateVariant(BUNDLE_VARIANT_ID);
    if (!variant || !variant.available) throw new Error("Bundle variant unavailable");

    // ✅ build price + properties
    let bundleTotalCents = 0;
    const properties = { _is_bundle: "true" };

    for (let i = 1; i <= this.totalBoxes; i++) {
      const p = this.selectedProducts["box" + i];
      if (!p) {
        alert("Please select all products");
        return;
      }
      bundleTotalCents += Math.round(Number(p.price) || 0);

      if (i === 1) properties["Candle"] = p.title;
      if (i === 2) properties["Scent"] = p.title;

      // optional: store ids too
      // properties[`Box ${i} Product ID`] = String(p.variantId || p.id || "");
    }

    // UI lock
    this.els.atcBtn.disabled = true;
    this.els.atcBtn.textContent = "Updating price...";

    // 1) ✅ update price
    await callProxy(bundleTotalCents);

    // 2) ✅ check cart to see if bundle line exists
    const cart = await getCart();

    // IMPORTANT: /cart.js items do NOT contain "line"
    // we compute line from array index (1-based)
    const existingIndex = cart.items.findIndex(
      (i) => String(i.id) === String(BUNDLE_VARIANT_ID)
    );
    const existingLine = existingIndex >= 0 ? existingIndex + 1 : null;

    // Dawn sections: send array (best compatibility)
    const payloadBase = {
      sections: ["cart-drawer", "cart-icon-bubble"],
      sections_url: window.location.pathname,
    };

    let res;

    if (existingLine) {
      // ✅ UPDATE existing line (keep quantity 1 and replace properties)
      res = await fetch("/cart/change.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          line: existingLine,
          quantity: 1,
          properties,
          ...payloadBase,
        }),
      });
    } else {
      // ✅ ADD new bundle line
      res = await fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: BUNDLE_VARIANT_ID,
          quantity: 1,
          properties,
          ...payloadBase,
        }),
      });
    }

    const data = await safeJson(res);
    if (!res.ok) {
      console.error("Cart error response:", data);
      throw new Error((data && data.description) || "Add to cart failed");
    }

    // 3) ✅ Proper Dawn drawer refresh
    // Dawn cart-drawer has renderContents(data)
    if (drawer && typeof drawer.renderContents === "function") {
      drawer.renderContents(data);
    } else if (data && data.sections) {
      // fallback (if theme changed)
      for (const [sectionId, html] of Object.entries(data.sections)) {
        const container = document.getElementById(sectionId);
        if (container) container.innerHTML = html;
      }
    }

    // 4) ✅ Ensure drawer opens AFTER DOM update
    await sleep(80);
    drawer?.open?.();

  } catch (e) {
    console.error("Bundle Add Error:", e);
    alert(e?.message || "Something went wrong");
  } finally {
    this.els.atcBtn.disabled = false;
    this.els.atcBtn.textContent = "Add to Cart";
  }
}


  escapeHtml(str) {
    return String(str || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.gift-box-builder-v2').forEach((root) => {
    new GiftBoxBuilderV2(root);
  });
});

document.addEventListener("DOMContentLoaded", () => {
  // ✅ DESKTOP ONLY
  if (!window.matchMedia("(min-width: 750px)").matches) return;

  const STICKY_POINT = 170; // px

  document.querySelectorAll(".gift-box-builder-v2").forEach((section) => {
    const el = section.querySelector(".boxes-and-action-row");
    if (!el) return;

    const onScroll = () => {
      if (window.scrollY >= STICKY_POINT) {
        el.classList.add("is-sticky");
      } else {
        el.classList.remove("is-sticky");
      }
    };

    // Run once + on scroll
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  });
});







// document.addEventListener("click", (e) => {
//   const trigger = e.target.closest(".product-info-trigger");

//   if (!trigger) {
//     document.querySelectorAll(".product-card-grid.is-tooltip-open")
//       .forEach(el => el.classList.remove("is-tooltip-open"));
//     return;
//   }

//   const mode = trigger.dataset.infoMode;
//   if (mode !== "click") return;

//   e.preventDefault();
//   const card = trigger.closest(".product-card-grid");
//   card.classList.toggle("is-tooltip-open");
// });
